import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const DiscountsTab = () => {
  const [discounts, setDiscounts] = useState([]);
  const [discountLocations, setDiscountLocations] = useState([]);
  const [discountFilter, setDiscountFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [newDiscount, setNewDiscount] = useState({
    nazwa: '', opis: '', typ_rabatu: 'procentowy', wartosc: 0, minimum_koszyka: 0,
    limit_miesieczny_aktywny: false, limit_miesieczny_kwota: 0, limit_miesieczny_ilosc: 0,
    limit_dzienny_aktywny: false, limit_dzienny_kwota: 0, limit_dzienny_ilosc: 0,
    aktywny: true, location_id: '', wymaga_kodu: false, wymaga_karty_klienta: false, wymaga_influencera: false
  });
  const [editingDiscount, setEditingDiscount] = useState(null);

  // --- API functions ---

  const loadDiscountLocations = useCallback(async () => {
    try {
      const response = await api.get('/locations/');
      const data = response.data;
      const locations = Array.isArray(data.data) ? data.data : (data.data?.locations || data.locations || []);
      const mappedLocations = locations.map(loc => ({
      ...loc,
      name: loc.name || loc.nazwa
      }));
      setDiscountLocations(mappedLocations);
    } catch (err) {
      console.error('Blad podczas pobierania lokalizacji:', err);
    }
  }, []);

  const loadDiscounts = useCallback(async () => {
    try {
      let url = '/rabaty';
      if (discountFilter === 'active') {
        url += '?aktywny=1';
      } else if (discountFilter === 'inactive') {
        url += '?aktywny=0';
      } else if (discountFilter === 'all') {
        url += '?aktywny=all';
      }

      const response = await api.get(url);
      const data = response.data;
      const discountsList = data.data?.rabaty || data.message?.rabaty || [];
      setDiscounts(discountsList);
    } catch (err) {
      console.error('Blad podczas pobierania rabatow:', err);
    } finally {
      setLoading(false);
    }
  }, [discountFilter]);

  const addDiscount = async () => {
    if (!newDiscount.nazwa.trim()) {
      toast.error('Nazwa rabatu jest wymagana');
      return;
    }
    if (newDiscount.wartosc <= 0) {
      toast.error('Wartosc rabatu musi byc wieksza od 0');
      return;
    }

    try {
      await api.post('/rabaty', newDiscount);

      setNewDiscount({
        nazwa: '', opis: '', typ_rabatu: 'procentowy', wartosc: 0, minimum_koszyka: 0,
        limit_miesieczny_aktywny: false, limit_miesieczny_kwota: 0, limit_miesieczny_ilosc: 0,
        limit_dzienny_aktywny: false, limit_dzienny_kwota: 0, limit_dzienny_ilosc: 0,
        aktywny: true, location_id: '', wymaga_kodu: false, wymaga_karty_klienta: false, wymaga_influencera: false
      });
      loadDiscounts();
      toast.success('Rabat zostal dodany pomyslnie');
    } catch (err) {
      console.error('Blad podczas dodawania rabatu:', err);
      toast.error(err.response?.data?.message || 'Blad podczas dodawania rabatu');
    }
  };

  const deleteDiscount = async (discountId) => {
    if (!window.confirm('Czy na pewno chcesz usunac ten rabat?')) {
      return;
    }

    try {
      await api.delete(`/rabaty/${discountId}`);

      await loadDiscounts();
      toast.success('Rabat zostal usuniety pomyslnie');
    } catch (err) {
      console.error('Blad podczas usuwania rabatu:', err);
      toast.error(err.response?.data?.message || 'Blad podczas usuwania rabatu');
    }
  };

  const startEditDiscount = (discount) => {
    setEditingDiscount({ ...discount });
  };

  const cancelEditDiscount = () => {
    setEditingDiscount(null);
  };

  const updateDiscount = async () => {
    if (!editingDiscount?.nazwa?.trim()) {
      toast.error('Nazwa rabatu jest wymagana');
      return;
    }
    if ((editingDiscount?.wartosc || 0) <= 0) {
      toast.error('Wartosc rabatu musi byc wieksza od 0');
      return;
    }

    try {
      await api.put(`/rabaty/${editingDiscount.id}`, editingDiscount);

      await loadDiscounts();
      setEditingDiscount(null);
      toast.success('Rabat zostal zaktualizowany pomyslnie');
    } catch (err) {
      console.error('Blad podczas aktualizacji rabatu:', err);
      toast.error(err.response?.data?.message || 'Blad podczas aktualizacji rabatu');
    }
  };

  // --- Effects ---

  useEffect(() => {
    loadDiscountLocations();
  }, [loadDiscountLocations]);

  useEffect(() => {
    loadDiscounts();
  }, [loadDiscounts]);

  // --- Render ---

  return (
    <div className="discounts-grid admin-table-wrap" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
      <style>{`
        @media (max-width: 900px) {
          .discounts-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .discounts-grid .discount-item { flex-direction: column !important; align-items: stretch !important; gap: 0.5rem !important; }
          .discounts-grid .discount-item .discount-actions { width: 100%; justify-content: flex-end; }
        }
      `}</style>
      <div>
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: '0.375rem',
          backgroundColor: 'var(--card-bg)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1rem',
            backgroundColor: 'var(--bg)',
            borderBottom: '1px solid var(--border)',
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>🎯 Zarzadzanie rabatami</span>
            <select
              value={discountFilter}
              onChange={(e) => setDiscountFilter(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                fontSize: '12px'
              }}
            >
              <option value="all">Wszystkie rabaty</option>
              <option value="active">Tylko aktywne</option>
              <option value="inactive">Tylko nieaktywne</option>
            </select>
          </div>

          <div style={{ padding: '1rem' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                <p>Ladowanie rabatow...</p>
              </div>
            ) : discounts.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: 'var(--text-muted)'
              }}>
                <p>Brak rabatow w systemie</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {discounts.map(discount => (
                  <div key={discount.id} className="discount-item" style={{
                    padding: '1rem',
                    border: '1px solid var(--border)',
                    borderRadius: '0.375rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: discount.aktywny ? 'var(--card-bg)' : 'var(--bg)'
                  }}>
                    {editingDiscount && editingDiscount.id === discount.id ? (
                      // Formularz edycji z opisami
                      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {/* Podstawowe */}
                        <div style={{ border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ padding: '6px 8px', border: '1px solid var(--info)', color: 'var(--info)' , fontSize: '11px', fontWeight: '600' }}>
                            Podstawowe
                          </div>
                          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div>
                              <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Nazwa rabatu</label>
                              <input
                                type="text"
                                value={editingDiscount?.nazwa || ''}
                                onChange={(e) => setEditingDiscount({...editingDiscount, nazwa: e.target.value})}
                                style={{ width: '100%', padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px' }}
                              />
                              <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>Unikalna nazwa wyswietlana przy wyborze rabatu</div>
                            </div>
                            <div>
                              <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Opis</label>
                              <textarea
                                value={editingDiscount?.opis || ''}
                                onChange={(e) => setEditingDiscount({...editingDiscount, opis: e.target.value})}
                                style={{ width: '100%', padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px', minHeight: '40px', resize: 'vertical' }}
                              />
                              <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>Dodatkowy opis dla ulatwienia identyfikacji</div>
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={editingDiscount?.aktywny || false}
                                onChange={(e) => setEditingDiscount({...editingDiscount, aktywny: e.target.checked})}
                              />
                              Aktywny
                            </label>
                          </div>
                        </div>

                        {/* Wartosc rabatu */}
                        <div style={{ border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ padding: '6px 8px', border: '1px solid var(--success)', color: 'var(--success)' , fontSize: '11px', fontWeight: '600' }}>
                            Wartosc rabatu
                          </div>
                          <div style={{ padding: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                            <div>
                              <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Typ</label>
                              <select
                                value={editingDiscount?.typ_rabatu || 'procentowy'}
                                onChange={(e) => setEditingDiscount({...editingDiscount, typ_rabatu: e.target.value})}
                                style={{ width: '100%', padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px' }}
                              >
                                <option value="procentowy">Procentowy (%)</option>
                                <option value="kwotowy">Kwotowy (zl)</option>
                              </select>
                              <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>% = rabat od wartosci koszyka, zl = stala kwota</div>
                            </div>
                            <div>
                              <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Wartosc</label>
                              <input
                                type="number"
                                value={editingDiscount?.wartosc || 0}
                                onChange={(e) => setEditingDiscount({...editingDiscount, wartosc: parseFloat(e.target.value) || 0})}
                                min="0"
                                step="0.01"
                                style={{ width: '100%', padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px' }}
                              />
                              <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>Np. 10 dla 10% lub 50 dla 50zl</div>
                            </div>
                          </div>
                        </div>

                        {/* Warunki uzycia */}
                        <div style={{ border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ padding: '6px 8px', border: '1px solid var(--info)', color: 'var(--info)' , fontSize: '11px', fontWeight: '600' }}>
                            Warunki uzycia
                          </div>
                          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div>
                              <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Min. wartosc koszyka (zl)</label>
                              <input
                                type="number"
                                value={editingDiscount?.minimum_koszyka || 0}
                                onChange={(e) => setEditingDiscount({...editingDiscount, minimum_koszyka: parseFloat(e.target.value) || 0})}
                                min="0"
                                step="0.01"
                                style={{ width: '100%', padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px' }}
                              />
                              <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>Rabat aktywuje sie dopiero gdy koszyk osiagnie te wartosc. 0 = bez minimum</div>
                            </div>
                            <div>
                              <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Lokalizacje</label>
                              <div style={{ border: '1px solid var(--border)', borderRadius: '3px', padding: '4px 6px', fontSize: '12px', maxHeight: '120px', overflowY: 'auto' }}>
                                {discountLocations.map(loc => {
                                  const ids = (editingDiscount?.location_ids ? (typeof editingDiscount.location_ids === 'string' ? JSON.parse(editingDiscount.location_ids) : editingDiscount.location_ids) : []) || [];
                                  const checked = ids.includes(loc.id);
                                  return (
                                    <label key={loc.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0', cursor: 'pointer', fontSize: '11px' }}>
                                      <input type="checkbox" checked={checked} onChange={() => {
                                        const updated = checked ? ids.filter(id => id !== loc.id) : [...ids, loc.id];
                                        setEditingDiscount({...editingDiscount, location_ids: updated, location_id: updated.length > 0 ? updated[0] : null});
                                      }} />
                                      {loc.nazwa}
                                    </label>
                                  );
                                })}
                              </div>
                              <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>Brak zaznaczenia = wszystkie lokalizacje</div>
                            </div>
                          </div>
                        </div>

                        {/* Limity */}
                        <div style={{ border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ padding: '6px 8px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' , fontSize: '11px', fontWeight: '600' }}>
                            Limity
                          </div>
                          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {/* Miesieczne */}
                            <div style={{ padding: '6px', backgroundColor: 'var(--bg)', borderRadius: '3px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={editingDiscount?.limit_miesieczny_aktywny || false}
                                  onChange={(e) => setEditingDiscount({...editingDiscount, limit_miesieczny_aktywny: e.target.checked})}
                                />
                                <span style={{ fontSize: '11px', fontWeight: '500' }}>Limity miesieczne</span>
                              </label>
                              {editingDiscount?.limit_miesieczny_aktywny && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginTop: '4px' }}>
                                  <div>
                                    <label style={{ fontSize: '9px', color: 'var(--text-secondary)' }}>Limit kwotowy (zl)</label>
                                    <input
                                      type="number"
                                      value={editingDiscount?.limit_miesieczny_kwota || 0}
                                      onChange={(e) => setEditingDiscount({...editingDiscount, limit_miesieczny_kwota: parseFloat(e.target.value) || 0})}
                                      min="0"
                                      step="0.01"
                                      style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
                                    />
                                    <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Max kwota koszyka do zrabatowania w miesiacu</div>
                                  </div>
                                  <div>
                                    <label style={{ fontSize: '9px', color: 'var(--text-secondary)' }}>Limit uzyc</label>
                                    <input
                                      type="number"
                                      value={editingDiscount?.limit_miesieczny_ilosc || 0}
                                      onChange={(e) => setEditingDiscount({...editingDiscount, limit_miesieczny_ilosc: parseInt(e.target.value) || 0})}
                                      min="0"
                                      style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
                                    />
                                    <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Ile razy mozna uzyc w miesiacu (0=bez limitu)</div>
                                  </div>
                                </div>
                              )}
                            </div>
                            {/* Dzienne */}
                            <div style={{ padding: '6px', backgroundColor: 'var(--bg)', borderRadius: '3px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={editingDiscount?.limit_dzienny_aktywny || false}
                                  onChange={(e) => setEditingDiscount({...editingDiscount, limit_dzienny_aktywny: e.target.checked})}
                                />
                                <span style={{ fontSize: '11px', fontWeight: '500' }}>Limity dzienne</span>
                              </label>
                              {editingDiscount?.limit_dzienny_aktywny && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginTop: '4px' }}>
                                  <div>
                                    <label style={{ fontSize: '9px', color: 'var(--text-secondary)' }}>Limit kwotowy (zl)</label>
                                    <input
                                      type="number"
                                      value={editingDiscount?.limit_dzienny_kwota || 0}
                                      onChange={(e) => setEditingDiscount({...editingDiscount, limit_dzienny_kwota: parseFloat(e.target.value) || 0})}
                                      min="0"
                                      step="0.01"
                                      style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
                                    />
                                    <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Max kwota koszyka do zrabatowania dziennie</div>
                                  </div>
                                  <div>
                                    <label style={{ fontSize: '9px', color: 'var(--text-secondary)' }}>Limit uzyc</label>
                                    <input
                                      type="number"
                                      value={editingDiscount?.limit_dzienny_ilosc || 0}
                                      onChange={(e) => setEditingDiscount({...editingDiscount, limit_dzienny_ilosc: parseInt(e.target.value) || 0})}
                                      min="0"
                                      style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
                                    />
                                    <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Ile razy mozna uzyc dziennie (0=bez limitu)</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Opcje weryfikacji */}
                        <div style={{ border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ padding: '6px 8px', backgroundColor: 'var(--warning)', color: '#000', fontSize: '11px', fontWeight: '600' }}>
                            Opcje weryfikacji
                          </div>
                          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={editingDiscount?.wymaga_kodu || false}
                                onChange={(e) => setEditingDiscount({...editingDiscount, wymaga_kodu: e.target.checked})}
                                style={{ marginTop: '2px' }}
                              />
                              <div>
                                <span style={{ fontSize: '11px', fontWeight: '500' }}>Wymagaj kodu weryfikacyjnego</span>
                                <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Kasjer musi wprowadzic 12-cyfrowy kod (np. ze skanera)</div>
                              </div>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={editingDiscount?.wymaga_karty_klienta || false}
                                onChange={(e) => setEditingDiscount({...editingDiscount, wymaga_karty_klienta: e.target.checked})}
                                style={{ marginTop: '2px' }}
                              />
                              <div>
                                <span style={{ fontSize: '11px', fontWeight: '500' }}>Wymagaj numeru karty klienta</span>
                                <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Kasjer musi wprowadzic numer karty klienta</div>
                              </div>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={editingDiscount?.wymaga_influencera || false}
                                onChange={(e) => setEditingDiscount({...editingDiscount, wymaga_influencera: e.target.checked})}
                                style={{ marginTop: '2px' }}
                              />
                              <div>
                                <span style={{ fontSize: '11px', fontWeight: '500' }}>Wymagaj nazwy influencera</span>
                                <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Kasjer musi wprowadzic nazwe influencera/polecajacego</div>
                              </div>
                            </label>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '4px' }}>
                          <button
                            onClick={cancelEditDiscount}
                            style={{ padding: '6px 12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' , cursor: 'pointer', fontSize: '12px' }}
                          >
                            Anuluj
                          </button>
                          <button
                            onClick={updateDiscount}
                            style={{ padding: '6px 12px', border: '1px solid var(--success)', color: 'var(--success)' , cursor: 'pointer', fontSize: '12px' }}
                          >
                            Zapisz
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Widok normalny
                      <>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: '0.25rem'
                          }}>
                            <span style={{
                              fontWeight: '600',
                              fontSize: '0.95rem',
                              color: discount.aktywny ? 'var(--text-secondary)' : 'var(--text-muted)'
                            }}>
                              {discount.nazwa}
                            </span>
                            <span style={{
                              padding: '0.125rem 0.375rem',
                              backgroundColor: discount.aktywny ? 'var(--success-light)' : 'var(--danger-light)',
                              color: discount.aktywny ? 'var(--success)' : 'var(--danger)',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}>
                              {discount.aktywny ? 'Aktywny' : 'Nieaktywny'}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                            {discount.typ_rabatu === 'procentowy' ? (
                              <span>{discount.wartosc}% rabatu</span>
                            ) : (
                              <span>{parseFloat(discount.wartosc).toFixed(2)} zl rabatu</span>
                            )}
                            {discount.minimum_koszyka > 0 && (
                              <span style={{ marginLeft: '0.5rem' }}>
                                (min. koszyk: {parseFloat(discount.minimum_koszyka).toFixed(2)} zl)
                              </span>
                            )}
                          </div>
                          {discount.opis && (
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                              {discount.opis}
                            </div>
                          )}
                          {(discount.location_ids || discount.location_id) && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--info)', marginTop: '0.25rem' }}>
                              Lokalizacje: {(() => {
                                const ids = discount.location_ids ? (typeof discount.location_ids === 'string' ? JSON.parse(discount.location_ids) : discount.location_ids) : (discount.location_id ? [discount.location_id] : []);
                                return ids.map(id => discountLocations.find(l => l.id === id)?.nazwa || `ID: ${id}`).join(', ');
                              })()}
                            </div>
                          )}
                          {!discount.location_id && !discount.location_ids && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: '0.25rem' }}>
                              Wszystkie lokalizacje
                            </div>
                          )}
                          {(discount.limit_miesieczny_aktywny || discount.limit_dzienny_aktywny) && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--info)', marginTop: '0.25rem' }}>
                              {discount.limit_miesieczny_aktywny && 'Limit miesieczny '}
                              {discount.limit_dzienny_aktywny && 'Limit dzienny'}
                            </div>
                          )}
                        </div>
                        <div className="discount-actions" style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => startEditDiscount(discount)}
                            style={{
                              padding: '4px 10px',
                              border: '1px solid var(--info)', color: 'var(--info)' ,
                              cursor: 'pointer',
                              fontSize: '11px'
                            }}
                          >
                            Edytuj
                          </button>
                          <button
                            onClick={() => deleteDiscount(discount.id)}
                            style={{
                              padding: '4px 10px',
                              border: '1px solid var(--danger)', color: 'var(--danger)' ,
                              cursor: 'pointer',
                              fontSize: '11px'
                            }}
                          >
                            Usun
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Panel dodawania nowego rabatu */}
      <div>
        <div style={{ border: '1px solid var(--border)', borderRadius: '4px', backgroundColor: 'var(--card-bg)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)', fontWeight: '600', fontSize: '13px' }}>
            Dodaj nowy rabat
          </div>

          <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Podstawowe */}
            <div style={{ border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ padding: '6px 8px', border: '1px solid var(--info)', color: 'var(--info)' , fontSize: '11px', fontWeight: '600' }}>
                Podstawowe
              </div>
              <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Nazwa rabatu *</label>
                  <input
                    type="text"
                    value={newDiscount.nazwa}
                    onChange={(e) => setNewDiscount({...newDiscount, nazwa: e.target.value})}
                    placeholder="np. Rabat weekend"
                    style={{ width: '100%', padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px' }}
                  />
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>Unikalna nazwa wyswietlana przy wyborze rabatu</div>
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Opis</label>
                  <textarea
                    value={newDiscount?.opis || ''}
                    onChange={(e) => setNewDiscount({...newDiscount, opis: e.target.value})}
                    placeholder="Opis rabatu..."
                    style={{ width: '100%', padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px', minHeight: '40px', resize: 'vertical' }}
                  />
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>Dodatkowy opis dla ulatwienia identyfikacji</div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newDiscount.aktywny}
                    onChange={(e) => setNewDiscount({...newDiscount, aktywny: e.target.checked})}
                  />
                  Aktywny
                </label>
              </div>
            </div>

            {/* Wartosc rabatu */}
            <div style={{ border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ padding: '6px 8px', border: '1px solid var(--success)', color: 'var(--success)' , fontSize: '11px', fontWeight: '600' }}>
                Wartosc rabatu
              </div>
              <div style={{ padding: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Typ</label>
                  <select
                    value={newDiscount.typ_rabatu}
                    onChange={(e) => setNewDiscount({...newDiscount, typ_rabatu: e.target.value})}
                    style={{ width: '100%', padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px' }}
                  >
                    <option value="procentowy">Procentowy (%)</option>
                    <option value="kwotowy">Kwotowy (zl)</option>
                  </select>
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>% = rabat od wartosci koszyka, zl = stala kwota</div>
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Wartosc *</label>
                  <input
                    type="number"
                    value={newDiscount.wartosc}
                    onChange={(e) => setNewDiscount({...newDiscount, wartosc: parseFloat(e.target.value) || 0})}
                    placeholder={newDiscount.typ_rabatu === 'procentowy' ? '10' : '50'}
                    min="0"
                    step="0.01"
                    style={{ width: '100%', padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px' }}
                  />
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>Np. 10 dla 10% lub 50 dla 50zl</div>
                </div>
              </div>
            </div>

            {/* Warunki uzycia */}
            <div style={{ border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ padding: '6px 8px', border: '1px solid var(--info)', color: 'var(--info)' , fontSize: '11px', fontWeight: '600' }}>
                Warunki uzycia
              </div>
              <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Min. wartosc koszyka (zl)</label>
                  <input
                    type="number"
                    value={newDiscount.minimum_koszyka}
                    onChange={(e) => setNewDiscount({...newDiscount, minimum_koszyka: parseFloat(e.target.value) || 0})}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    style={{ width: '100%', padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px' }}
                  />
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>Rabat aktywuje sie dopiero gdy koszyk osiagnie te wartosc. 0 = bez minimum</div>
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)' }}>Lokalizacje</label>
                  <div style={{ border: '1px solid var(--border)', borderRadius: '3px', padding: '4px 6px', fontSize: '12px', maxHeight: '120px', overflowY: 'auto' }}>
                    {discountLocations.map(loc => {
                      const ids = newDiscount.location_ids || [];
                      const checked = ids.includes(loc.id);
                      return (
                        <label key={loc.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0', cursor: 'pointer', fontSize: '11px' }}>
                          <input type="checkbox" checked={checked} onChange={() => {
                            const updated = checked ? ids.filter(id => id !== loc.id) : [...ids, loc.id];
                            setNewDiscount({...newDiscount, location_ids: updated, location_id: updated.length > 0 ? updated[0] : null});
                          }} />
                          {loc.nazwa}
                        </label>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>Brak zaznaczenia = wszystkie lokalizacje</div>
                </div>
              </div>
            </div>

            {/* Limity */}
            <div style={{ border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ padding: '6px 8px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' , fontSize: '11px', fontWeight: '600' }}>
                Limity
              </div>
              <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {/* Miesieczne */}
                <div style={{ padding: '6px', backgroundColor: 'var(--bg)', borderRadius: '3px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newDiscount.limit_miesieczny_aktywny}
                      onChange={(e) => setNewDiscount({...newDiscount, limit_miesieczny_aktywny: e.target.checked})}
                    />
                    <span style={{ fontSize: '11px', fontWeight: '500' }}>Limity miesieczne</span>
                  </label>
                  {newDiscount.limit_miesieczny_aktywny && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginTop: '4px' }}>
                      <div>
                        <label style={{ fontSize: '9px', color: 'var(--text-secondary)' }}>Limit kwotowy (zl)</label>
                        <input
                          type="number"
                          value={newDiscount.limit_miesieczny_kwota}
                          onChange={(e) => setNewDiscount({...newDiscount, limit_miesieczny_kwota: parseFloat(e.target.value) || 0})}
                          min="0"
                          step="0.01"
                          style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
                        />
                        <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Max kwota koszyka do zrabatowania w miesiacu</div>
                      </div>
                      <div>
                        <label style={{ fontSize: '9px', color: 'var(--text-secondary)' }}>Limit uzyc</label>
                        <input
                          type="number"
                          value={newDiscount.limit_miesieczny_ilosc}
                          onChange={(e) => setNewDiscount({...newDiscount, limit_miesieczny_ilosc: parseInt(e.target.value) || 0})}
                          min="0"
                          style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
                        />
                        <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Ile razy mozna uzyc w miesiacu (0=bez limitu)</div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Dzienne */}
                <div style={{ padding: '6px', backgroundColor: 'var(--bg)', borderRadius: '3px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newDiscount.limit_dzienny_aktywny}
                      onChange={(e) => setNewDiscount({...newDiscount, limit_dzienny_aktywny: e.target.checked})}
                    />
                    <span style={{ fontSize: '11px', fontWeight: '500' }}>Limity dzienne</span>
                  </label>
                  {newDiscount.limit_dzienny_aktywny && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginTop: '4px' }}>
                      <div>
                        <label style={{ fontSize: '9px', color: 'var(--text-secondary)' }}>Limit kwotowy (zl)</label>
                        <input
                          type="number"
                          value={newDiscount.limit_dzienny_kwota}
                          onChange={(e) => setNewDiscount({...newDiscount, limit_dzienny_kwota: parseFloat(e.target.value) || 0})}
                          min="0"
                          step="0.01"
                          style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
                        />
                        <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Max kwota koszyka do zrabatowania dziennie</div>
                      </div>
                      <div>
                        <label style={{ fontSize: '9px', color: 'var(--text-secondary)' }}>Limit uzyc</label>
                        <input
                          type="number"
                          value={newDiscount.limit_dzienny_ilosc}
                          onChange={(e) => setNewDiscount({...newDiscount, limit_dzienny_ilosc: parseInt(e.target.value) || 0})}
                          min="0"
                          style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
                        />
                        <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Ile razy mozna uzyc dziennie (0=bez limitu)</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Opcje weryfikacji */}
            <div style={{ border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ padding: '6px 8px', backgroundColor: 'var(--warning)', color: '#000', fontSize: '11px', fontWeight: '600' }}>
                Opcje weryfikacji
              </div>
              <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newDiscount?.wymaga_kodu || false}
                    onChange={(e) => setNewDiscount({...newDiscount, wymaga_kodu: e.target.checked})}
                    style={{ marginTop: '2px' }}
                  />
                  <div>
                    <span style={{ fontSize: '11px', fontWeight: '500' }}>Wymagaj kodu weryfikacyjnego</span>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Kasjer musi wprowadzic 12-cyfrowy kod (np. ze skanera)</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newDiscount?.wymaga_karty_klienta || false}
                    onChange={(e) => setNewDiscount({...newDiscount, wymaga_karty_klienta: e.target.checked})}
                    style={{ marginTop: '2px' }}
                  />
                  <div>
                    <span style={{ fontSize: '11px', fontWeight: '500' }}>Wymagaj numeru karty klienta</span>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Kasjer musi wprowadzic numer karty klienta</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newDiscount?.wymaga_influencera || false}
                    onChange={(e) => setNewDiscount({...newDiscount, wymaga_influencera: e.target.checked})}
                    style={{ marginTop: '2px' }}
                  />
                  <div>
                    <span style={{ fontSize: '11px', fontWeight: '500' }}>Wymagaj nazwy influencera</span>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Kasjer musi wprowadzic nazwe influencera/polecajacego</div>
                  </div>
                </label>
              </div>
            </div>

            <button
              onClick={addDiscount}
              disabled={!newDiscount.nazwa.trim() || newDiscount.wartosc <= 0}
              style={{
                width: '100%',
                padding: '6px 12px',
                backgroundColor: !newDiscount.nazwa.trim() || newDiscount.wartosc <= 0 ? 'var(--text-muted)' : 'var(--success)',
                color: '#fff' ,
                cursor: !newDiscount.nazwa.trim() || newDiscount.wartosc <= 0 ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Dodaj rabat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountsTab;
